import {
  FC,
  JSXElementConstructor,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';

import { isEmpty, toPairs } from 'lodash';
import { useFormContext } from 'react-hook-form';

import { getTitleByRelation } from '@configs/adaptive-proposals/health/care/constants';
import { AdaptiveFormConfig, Fields } from '@elements';
import { HealthQuoteMember } from '@store/healthquote';
import { useProposalStore } from '@store/proposal';
import { InsuredMemberBase } from '@store/proposal/types';
import { dayjs, Group, Input, Stack } from '@ui';
import { isMinor } from '@utils/dateUtils';

import { NomineeItem } from './NomineeItem';

type MemberIdentifierKeys = keyof Pick<
  InsuredMemberBase,
  'full_name' | 'relation' | 'dob'
>;
const DEFAULT_MEMBER_IDENTIFIERS: MemberIdentifierKeys[] = [
  'full_name',
  'relation',
  'dob'
];

export interface NomineeSelectProps {
  name: string;
  label?: string | ReactElement<any, string | JSXElementConstructor<any>>;
  fields: AdaptiveFormConfig;
  withOtherOption?: boolean;
  memberIdentifiers?: MemberIdentifierKeys[];
  addTitle?: boolean; // To add title field.
  unsupportedMembers?: HealthQuoteMember[]; // Member relations that should not be selectable as nominees.
  // If provided, these address fields will be set to the nominee and appointee (if applicable) when a member is selected.
  addressFields?: Record<string, string | number | null>;
  // Extra fields (relative to `name`) to clear when the user changes the selected member. Key is the field path, value is the reset value.
  additionalFieldsToClear?: Record<string, any>;
}

/**
 * NomineeSelect component allows users to select a nominee from a list of insured members or add a custom nominee.
 * It works assuming that the proposal form has a structure where each member has a unique `member_id` and the insured_basic array contains all members with
 * the necessary details like `full_name`, `relation`, and `dob`, if this is not the case, the component may need adjustments.
 * If this functionality is not required it might be better to add the config for each fields directly in the step config.
 *
 * logic for handling minor appointee fields and validation has to be in the adaptive form config itself.
 */
const NomineeSelect: FC<NomineeSelectProps> = ({
  name,
  fields,
  withOtherOption = true,
  memberIdentifiers = DEFAULT_MEMBER_IDENTIFIERS,
  addTitle = false,
  unsupportedMembers = [],
  addressFields,
  additionalFieldsToClear = {}
}) => {
  const [selectedMember, setSelectedMember] = useState<string>();
  const isInitialLoad = useRef(true);

  const proposalForm = useProposalStore((state) => state.proposalForm);
  const insuredMembers: InsuredMemberBase[] = proposalForm?.insured_basic;

  const {
    setValue,
    clearErrors,
    formState: { errors }
  } = useFormContext();

  // Initialize selectedMember based on the proposal form data if the step is revisited
  useEffect(() => {
    if (proposalForm[name]) {
      const memberId = insuredMembers.find((m) =>
        memberIdentifiers.every((key) => m[key] === proposalForm[name][key])
      )?.member_id;

      if (memberId) {
        setSelectedMember(memberId);
      } else if (proposalForm[name]?.full_name) {
        setSelectedMember('other');
      }
    } else if (filteredInsuredMembers.length === 0 && withOtherOption) {
      // Only default to 'other' if there's no existing form data AND no members
      setSelectedMember('other');
    }
  }, [proposalForm[name]]);

  // Update form values when selectedMember changes, we copy the member details to the form fields
  useEffect(() => {
    if (selectedMember) {
      // Clear any existing errors when a selection is made
      clearErrors(name);

      if (selectedMember !== 'other') {
        const member = insuredMembers.find((m) => m.member_id === selectedMember);
        if (member) {
          if (!isInitialLoad.current) {
            // Member changed from UI: reset nominee
            setValue(name, member.member_id);
            if (!isEmpty(addressFields)) {
              setValue(`${name}.address_same_as_proposer`, null);
              setValue(`${name}.address`, addressFields);
              setValue(`${name}.appointee.address_same_as_proposer`, '');
              setValue(`${name}.appointee.address`, addressFields);
            }
            if (!isEmpty(additionalFieldsToClear)) {
              toPairs(additionalFieldsToClear).forEach(
                ([field, value]: [string, any]) => {
                  setValue(`${name}.${field}`, value);
                }
              );
            }
          }
          if (addTitle) {
            setValue(
              `${name}.title`,
              getTitleByRelation(member.relation, proposalForm?.proposer?.gender) ||
                ''
            );
          }

          setValue(`${name}.full_name`, member.full_name);
          setValue(`${name}.relation`, member.relation);
          setValue(`${name}.dob`, member.dob);
          setValue(`${name}.age`, dayjs().diff(member.dob, 'years'));

          // Handle appointee values based on whether this is initial load and member is minor
          const memberIsMinor = isMinor(member.dob);

          if (
            isInitialLoad.current &&
            memberIsMinor &&
            proposalForm?.[name] &&
            proposalForm[name]?.appointee
          ) {
            // Initial load: keep existing appointee values if member is minor
            setValue(
              `${name}.appointee.full_name`,
              proposalForm[name].appointee.full_name
            );
            setValue(
              `${name}.appointee.relation`,
              proposalForm[name].appointee.relation
            );
            setValue(`${name}.appointee.dob`, proposalForm[name].appointee.dob);
          } else if (!isInitialLoad.current) {
            // Member changed from UI: clear appointee values
            setValue(`${name}.appointee.full_name`, '');
            setValue(`${name}.appointee.relation`, '');
            setValue(`${name}.appointee.dob`, null);
            if (!isEmpty(addressFields)) {
              setValue(`${name}.appointee.address_same_as_proposer`, null);
              setValue(`${name}.appointee.address`, addressFields);
            }
          } else if (memberIsMinor && proposalForm[name]?.appointee) {
            // Fallback: set appointee values if they exist and member is minor
            setValue(
              `${name}.appointee.full_name`,
              proposalForm[name].appointee.full_name
            );
            setValue(
              `${name}.appointee.relation`,
              proposalForm[name].appointee.relation
            );
            setValue(`${name}.appointee.dob`, proposalForm[name].appointee.dob);
            if (!isEmpty(addressFields)) {
              setValue(
                `${name}.appointee.address_same_as_proposer`,
                proposalForm[name].appointee?.address_same_as_proposer
              );
              setValue(
                `${name}.appointee.address`,
                proposalForm[name].appointee?.address
              );
            }
          } else {
            // Clear appointee values if not present or member is not minor
            setValue(`${name}.appointee.full_name`, '');
            setValue(`${name}.appointee.relation`, '');
            setValue(`${name}.appointee.dob`, null);
            if (!isEmpty(addressFields)) {
              setValue(`${name}.appointee.address_same_as_proposer`, null);
              setValue(`${name}.appointee.address`, addressFields);
            }
          }
        }
      } else {
        // Handle "other" option
        const nominee = proposalForm[name];

        //To identify whether the Other option is selected or not only for cases where addTitle is true.
        if (addTitle) {
          setValue(`${name}.otherOption`, 'true');
        }

        if (isInitialLoad.current) {
          // Initial load: keep existing values
          setValue(`${name}.full_name`, nominee?.full_name || '');
          setValue(`${name}.relation`, nominee?.relation || '');
          setValue(`${name}.dob`, nominee?.dob || null);
          setValue(`${name}.age`, nominee?.age || null);
          setValue(
            `${name}.appointee.full_name`,
            nominee?.appointee?.full_name || ''
          );
          setValue(`${name}.appointee.relation`, nominee?.appointee?.relation || '');
          setValue(`${name}.appointee.dob`, nominee?.appointee?.dob || null);
          if (!isEmpty(addressFields)) {
            setValue(
              `${name}.address_same_as_proposer`,
              nominee?.address_same_as_proposer ?? null
            );
            setValue(`${name}.address`, nominee?.address ?? addressFields);
            setValue(
              `${name}.appointee.address_same_as_proposer`,
              nominee?.appointee?.address_same_as_proposer ?? null
            );
            setValue(
              `${name}.appointee.address`,
              nominee?.appointee?.address ?? addressFields
            );
          }
        } else {
          // Member changed from UI: clear all values
          setValue(name, '');
          setValue(`${name}.title`, '');
          setValue(`${name}.full_name`, '');
          setValue(`${name}.relation`, '');
          setValue(`${name}.dob`, null);
          setValue(`${name}.age`, null);
          setValue(`${name}.appointee.full_name`, '');
          setValue(`${name}.appointee.relation`, '');
          setValue(`${name}.appointee.dob`, null);
          if (!isEmpty(addressFields)) {
            setValue(`${name}.address_same_as_proposer`, null);
            setValue(`${name}.address`, addressFields);
            setValue(`${name}.appointee.address_same_as_proposer`, null);
            setValue(`${name}.appointee.address`, addressFields);
          }
          if (!isEmpty(additionalFieldsToClear)) {
            toPairs(additionalFieldsToClear).forEach(
              ([field, value]: [string, any]) => {
                setValue(`${name}.${field}`, value);
              }
            );
          }
        }
      }

      // Mark that initial load is complete
      isInitialLoad.current = false;
    }
  }, [selectedMember, setValue, clearErrors, name, insuredMembers, proposalForm]);

  // Clear errors when component unmounts or when no member is selected
  useEffect(() => {
    if (!selectedMember) {
      clearErrors(name);
    }
  }, [selectedMember, clearErrors, name]);

  const filteredInsuredMembers = insuredMembers?.filter(
    (member) =>
      member.relation !== HealthQuoteMember.Self &&
      !unsupportedMembers.includes(member.relation)
  );

  return (
    <Stack w="100%" justify="center" align="center" gap={0}>
      {filteredInsuredMembers.map((member, index) => (
        <NomineeItem
          key={index}
          name={name}
          member={member}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          hasError={Boolean(errors?.[name]) && member.member_id === selectedMember}
          fields={[
            {
              type: Fields.memberDetails,
              componentProps: { member }
            },
            ...(fields || [])
          ]}
        />
      ))}
      {withOtherOption && (
        <NomineeItem
          key="other"
          name={name}
          member={{
            full_name: 'Other',
            relation: 'Other',
            member_id: 'other',
            dob: null
          }}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          fields={fields.map((field) => ({ ...field, hidden: false }))}
          hasError={Boolean(errors?.[name]) && selectedMember === 'other'}
        />
      )}
      {/* Show error if there are any errors under nominee key or if a member is not selected */}
      {errors?.[name] && (
        <Group justify="flex-start" w="100%" py="sm">
          <Input.Error size="xl">
            {selectedMember === 'other'
              ? 'Please fill all required nominee details.'
              : !selectedMember
              ? 'Please select a nominee.'
              : ''}
          </Input.Error>
        </Group>
      )}
    </Stack>
  );
};

export { NomineeSelect };
